import React, { useRef } from 'react';
import { X, Trash2, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ColorPicker from './ColorPicker';
import { getText } from './constants';

export const EditNoteDialog = ({
  isOpen,
  onOpenChange,
  language,
  note,
  setNote,
  onSave,
  onDelete,
  onMarkDone,
  onImageSelect
}) => {
  const fileInputRef = useRef(null);

  if (!note) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="edit-note-description">
        <DialogHeader>
          <DialogTitle>{getText('editNote', language)}</DialogTitle>
          <DialogDescription id="edit-note-description">
            {getText('dialogDescription', language)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="edit-note-content">{getText('content', language)}</Label>
            <Input
              id="edit-note-content"
              value={note.content || ''}
              onChange={(e) => setNote({ ...note, content: e.target.value })}
              placeholder={getText('placeholder', language)}
              maxLength={200}
              data-testid="edit-note-content"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="edit-note-category">{getText('category', language)}</Label>
            <Input
              id="edit-note-category"
              value={note.category || ''}
              onChange={(e) => setNote({ ...note, category: e.target.value })}
              placeholder={getText('categoryPlaceholder', language)}
              maxLength={30}
              data-testid="edit-note-category"
            />
          </div>

          <div className="space-y-2">
            <Label>{getText('color', language)}</Label>
            <ColorPicker 
              value={note.color || 'yellow'} 
              onChange={(color) => setNote({ ...note, color })} 
              language={language}
            />
          </div>
          
          {/* Image section */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              {language === 'de' ? 'Bild' : 'Image'}
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onImageSelect}
              className="hidden"
              data-testid="edit-note-image-input"
            />
            {note.image ? (
              <div className="relative">
                <img src={note.image} alt="Note" className="w-full h-32 object-cover rounded-lg" />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8"
                  onClick={() => setNote({ ...note, image: '' })}
                  data-testid="edit-remove-image-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-dashed"
                data-testid="edit-add-image-btn"
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                {getText('addImage', language)}
              </Button>
            )}
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-3 sm:justify-between">
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button 
                variant="outline"
                size="sm"
                className="text-green-600 border-green-300 hover:bg-green-50 w-full sm:w-auto"
                onClick={() => onMarkDone(note.id)}
                data-testid="mark-done-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                {getText('markDone', language)}
              </Button>
              <Button 
                variant="destructive"
                size="sm"
                className="w-full sm:w-auto"
                onClick={() => onDelete(note.id)}
                data-testid="delete-note-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {getText('delete', language)}
              </Button>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => onOpenChange(false)}>
                {getText('cancel', language)}
              </Button>
              <Button size="sm" className="flex-1 sm:flex-none bg-[#006039] hover:bg-[#004d2e]" onClick={onSave} data-testid="save-edit-note">
                {getText('save', language)}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditNoteDialog;
